import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutContent } from '@/mocks/movies';
import { useRef, useState } from 'react';

function AboutSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91精品影视',
    url: 'https://www.91jpys.com/',
    description: aboutContent.intro,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '光华路9号',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
    },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    const textarea = form.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    try {
      const resp = await fetch('https://readdy.ai/api/form/d9ocev0bdkpk99j7gu8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* raw text */ }

      const code = parsed?.code;
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string || (parsed as Record<string, unknown>)?.message as string || '';

      if (resp.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <AboutSchema />
      <Navbar />

      <main>
        <div className="pt-20 pb-6 px-4 md:px-6 mx-auto" style={{ maxWidth: '1400px' }}>
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative px-4 md:px-6 py-16 md:py-24 mx-auto overflow-hidden" style={{ maxWidth: '1400px' }}>
          <div className="absolute inset-0 rounded-2xl overflow-hidden mx-4 md:mx-6">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20workspace%20with%20warm%20lighting%2C%20creative%20team%20collaboration%2C%20minimalist%20design%20with%20wooden%20elements%2C%20cozy%20atmosphere%2C%20professional%20yet%20welcoming%2C%20cinematic%20photography%2C%20soft%20natural%20light&width=1400&height=400&seq=about-hero&orientation=landscape"
              alt="关于91精品影视"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
          </div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              关于91精品影视
            </h1>
            <p className="text-white/80 text-sm md:text-base leading-relaxed">
              {aboutContent.intro.slice(0, 120)}...
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="px-4 md:px-6 py-10 md:py-14 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {aboutContent.stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60"
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-foreground-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Story */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              我们的故事
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>{aboutContent.intro}</p>
              <p>{aboutContent.mission}</p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="px-4 md:px-6 py-10 md:py-14 mx-auto" style={{ maxWidth: '1400px' }}>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
            核心价值观
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 transition-colors duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className={`text-lg ${
                    ['ri-stack-line', 'ri-flashlight-line', 'ri-heart-line', 'ri-user-star-line'][index]
                  }`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-800 mb-2 text-sm">
                  {value.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/70 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">contact@91jpys.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">客服电话</h4>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-500">北京市朝阳区光华路9号</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  ref={formRef}
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="您的留言（500字以内）"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-emerald-600 text-center">留言已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}